/**
 * The podium plot's two sizes (plots plan E).
 *
 * `slide` carries the deck's numbers (a 1400x787 frame, scaled by
 * `SlideFrame`); `card` carries the summary column's. Every varying number is
 * here rather than in the markup, so the two sizes cannot drift into two
 * layouts. Every rank gets the same frame (owner, 2026-10-14): the rank reads
 * from the order, the chip and the score bar, never from a smaller picture.
 */

export type PodiumPlotSize = 'slide' | 'card'

export interface PodiumPlotMetrics {
  /** Every entrant's frame, width over height. */
  frameAspect: number
  /** The widest a frame grows; a two-entrant board centres instead of stretching. */
  maxFrameWidth: number
  /** Frames per row: the card wraps past it, the slide shows one row of at most this many. */
  maxColumns: number
  gap: number
  entryGap: number
  badgeInset: number
  winnerFontSize: number
  winnerPadding: string
  rankFontSize: number
  rankPadding: string
  labelFontSize: number
  scoreFontSize: number
  tieFontSize: number
  tiePadding: string
  singleTile: number
  singleLabelFontSize: number
  singleScoreFontSize: number
  playSize: number
  expandSize: number
  /**
   * Break a word wider than its tile and let the crown wrap inside the tile.
   * The slide's tiles never need it; a card's tiles share a narrow column.
   */
  fitNarrowTiles: boolean
  /**
   * With no picture and no copy, the tile names the item inside its frame
   * (the deck's tile, on the slide and the card alike), clear of the badges.
   */
  placeholderNameLines: number
  placeholderNamePadding: string
  /**
   * An imageless item whose respondents read copy (`respondentCopy`) shows
   * those words in its tile as a pull quote (owner, 2026-10-18): a quote mark,
   * then the copy in the deck's serif, in the same frame as a picture. Every
   * tile keeps the frame, so a row of tiles is one height: a copy longer than
   * its tile is clamped with an ellipsis and opens whole on click. Python logs
   * a clamped copy (`slide_content_overflow`) from the slide's measured box.
   */
  copyFontSize: number
  copyLineHeight: number
  copyPadding: string
  quoteMarkSize: number
}

export const PODIUM_PLOT_METRICS: Record<PodiumPlotSize, PodiumPlotMetrics> = {
  slide: {
    frameAspect: 4 / 3,
    // 440 px tall at two entrants: the board area under the title.
    maxFrameWidth: 587,
    maxColumns: 6,
    gap: 20,
    entryGap: 10,
    badgeInset: 10,
    winnerFontSize: 12,
    winnerPadding: '4px 12px',
    rankFontSize: 11,
    rankPadding: '3px 9px',
    labelFontSize: 16,
    scoreFontSize: 14,
    tieFontSize: 13,
    tiePadding: '6px 16px',
    singleTile: 96,
    singleLabelFontSize: 18,
    singleScoreFontSize: 13,
    playSize: 44,
    expandSize: 28,
    fitNarrowTiles: false,
    placeholderNameLines: 6,
    placeholderNamePadding: '44px 16px 16px',
    // Mirrored by `canonical_deck/slides/podium.py` (`_COPY_*`), whose fit
    // check measures the copy against this box.
    copyFontSize: 20,
    copyLineHeight: 1.35,
    copyPadding: '38px 20px 18px',
    quoteMarkSize: 48,
  },
  card: {
    frameAspect: 4 / 3,
    maxFrameWidth: 300,
    maxColumns: 4,
    gap: 12,
    entryGap: 6,
    badgeInset: 6,
    winnerFontSize: 10,
    winnerPadding: '2px 8px',
    rankFontSize: 10,
    rankPadding: '2px 6px',
    labelFontSize: 12,
    scoreFontSize: 11,
    tieFontSize: 11,
    tiePadding: '3px 10px',
    singleTile: 72,
    singleLabelFontSize: 14,
    singleScoreFontSize: 11,
    playSize: 28,
    expandSize: 20,
    fitNarrowTiles: true,
    placeholderNameLines: 4,
    placeholderNamePadding: '28px 10px 10px',
    copyFontSize: 13,
    copyLineHeight: 1.35,
    copyPadding: '24px 10px 10px',
    quoteMarkSize: 30,
  },
}

/**
 * The surface colours per size. A slide loads deck-ui's `tokens.css`; a card
 * is drawn on the report page, so it reads the report's own theme-aware
 * tokens first.
 */
export interface PodiumPlotPalette {
  cardBg: string
  border: string
  chipBg: string
  chipFg: string
  text: string
  muted: string
  bar: string
  barTrack: string
}

export const PODIUM_PLOT_PALETTE: Record<PodiumPlotSize, PodiumPlotPalette> = {
  slide: {
    cardBg: 'var(--slide-neutral-light, #EBEBEB)',
    border: 'var(--slide-border-soft, #E5E5E5)',
    chipBg: 'rgba(255, 255, 255, 0.88)',
    chipFg: 'var(--slide-text-primary, #1A1A1A)',
    text: 'var(--slide-text-primary, #1A1A1A)',
    muted: 'var(--slide-text-muted, #737373)',
    bar: 'var(--ds-deck-data-accent, var(--slide-accent, #0EBFA5))',
    barTrack: 'var(--slide-neutral-light, #EBEBEB)',
  },
  card: {
    cardBg: 'var(--slide-neutral-light, hsl(var(--report-surface)))',
    border: 'var(--slide-border-soft, hsl(var(--border)))',
    chipBg: 'hsl(var(--card) / 0.88)',
    chipFg: 'hsl(var(--foreground))',
    text: 'hsl(var(--foreground))',
    muted: 'hsl(var(--muted-foreground))',
    bar: 'var(--ds-deck-data-accent, var(--slide-accent, #0EBFA5))',
    barTrack: 'hsl(var(--muted))',
  },
}

export const PODIUM_ACCENT = 'var(--ds-deck-data-accent, var(--slide-accent, #0EBFA5))'
export const PODIUM_ACCENT_SOFT = 'var(--ds-surface-highlight, #e3ffea)'
/** The pull quote's face: the deck's serif, as its quotes and headlines use it. */
export const PODIUM_QUOTE_FONT = 'Sumana, Georgia, serif'
